'use client';

import { useState, type ReactNode } from 'react';
import {
  PRESET_KEYS,
  PRESET_LABEL,
  clampToBand,
  evaluateCeiling,
  formatAltitude,
  fromDisplay,
  sliderBand,
  toDisplay,
  type AltitudeProfile,
  type AltitudeUnit,
} from '@/lib/altitude';

const UNITS: readonly AltitudeUnit[] = ['ft', 'm'];

const UNIT_LABEL: Record<AltitudeUnit, string> = { ft: 'Ft', m: 'M' };

interface AltitudeControlProps {
  id?: string;
  profile: AltitudeProfile;
  /** Label for whoever owns the ceiling, e.g. "FAA Part 107". Null if unknown. */
  authority: string | null;
  valueMeters: number;
  onChange: (meters: number) => void;
  unit: AltitudeUnit;
  onUnitChange: (unit: AltitudeUnit) => void;
  /** Point launches finish on a hover and never send an RTH altitude. */
  showRth?: boolean;
  disabled?: boolean;
  /**
   * What the altitude is measured from. The dock pad is only the datum when the
   * aircraft is launching from a dock — a hand takeoff or an in-flight
   * reposition on the COP has a different one, and naming the wrong datum on a
   * flight control is worse than naming none.
   */
  reference?: string;
  /**
   * Accessible name for the slider and the exact field. Defaults to the dock
   * launch wording; the COP overrides it so a screen reader announces the
   * command actually being set rather than a "launch" that isn't one.
   */
  label?: string;
  /** Rendered below the controls — the launch card puts its default toggle here. */
  footer?: ReactNode;
}

/**
 * Altitude picker for the dock launch card — presets, a coarse slider and an
 * exact field, all bound to one value so any of the three can drive it and they
 * never disagree.
 *
 * Deliberately inline rather than a dialog: the operator has just placed a
 * target on the map, and covering the map to ask about altitude interrupts the
 * task it belongs to. Every bound, preset and threshold comes from `profile`.
 */
export function AltitudeControl({
  id,
  profile,
  authority,
  valueMeters,
  onChange,
  unit,
  onUnitChange,
  showRth = true,
  disabled = false,
  reference = 'AGL from dock pad',
  label = 'Launch altitude',
  footer,
}: AltitudeControlProps) {
  // While the field has focus the operator's keystrokes win, so typing "150"
  // isn't clamped to "1" mid-entry. Null means "follow the committed value".
  const [draft, setDraft] = useState<string | null>(null);

  // The operator's unit toggle is local to this control and may differ from the
  // org's stored preference, so everything derived — band, ceiling wording —
  // reads the unit actually on screen.
  const active = { ...profile, unit };
  // The held value is passed in so the band always covers it. Without this a
  // caller that seeds from something other than the profile — the COP's GO-TO
  // seeds from the aircraft's live altitude — gets its value pinned for display
  // while it still holds and sends the original, i.e. this control shows one
  // altitude and the aircraft flies another.
  const band = sliderBand(active, valueMeters);
  // Clamped, not snapped: the slider and the field must show the same number,
  // and snapping here would round a precise typed value off under the operator.
  const displayValue = clampToBand(toDisplay(valueMeters, unit), band);
  // Only the track colour here — the textual ceiling notice lives on the launch
  // card, which shows it whether or not this picker is expanded.
  const overCeiling = evaluateCeiling(valueMeters, active, authority).status !== 'ok';

  const commit = (nextDisplay: number) => {
    onChange(fromDisplay(clampToBand(nextDisplay, band), unit));
  };

  const commitDraft = () => {
    const parsed = Number(draft);
    if (draft !== null && draft.trim() !== '' && Number.isFinite(parsed)) commit(parsed);
    setDraft(null);
  };

  const ceilingDisplay = toDisplay(profile.ceiling_m, unit);
  const ceilingInBand = ceilingDisplay >= band.min && ceilingDisplay <= band.max;
  const ceilingPercent = ((ceilingDisplay - band.min) / (band.max - band.min)) * 100;

  const converted = unit === 'ft'
    ? formatAltitude(valueMeters, 'm')
    : formatAltitude(valueMeters, 'ft');

  return (
    <div id={id} className="space-y-2.5 rounded-md border border-subtle surface-elevated p-2.5">
      <div className="flex items-center gap-2">
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
          Altitude · {reference}
        </span>
        <div
          role="group"
          aria-label="Altitude unit"
          className="ml-auto flex gap-0.5 rounded border border-subtle bg-background p-0.5"
        >
          {UNITS.map((next) => (
            <button
              key={next}
              type="button"
              disabled={disabled}
              aria-pressed={unit === next}
              onClick={() => { setDraft(null); onUnitChange(next); }}
              className={`min-h-6 rounded px-2.5 py-1 text-[10px] uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 ${
                unit === next
                  ? 'bg-primary/20 text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {UNIT_LABEL[next]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {PRESET_KEYS.map((key) => {
          const presetMeters = profile.presets[key];
          const selected = presetMeters === valueMeters;
          const isDefault = presetMeters === profile.default_m;
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              aria-label={`${PRESET_LABEL[key]} altitude, ${formatAltitude(presetMeters, unit)}${isDefault ? ', organization default' : ''}`}
              onClick={() => { setDraft(null); onChange(presetMeters); }}
              className={`relative flex flex-col items-center gap-0 rounded border px-1 py-1.5 font-mono tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 ${
                selected
                  ? 'border-primary/45 bg-primary/15 text-primary'
                  : 'border-subtle bg-background text-foreground hover:border-hover'
              }`}
            >
              <span className="text-base font-semibold leading-tight">
                {toDisplay(presetMeters, unit)}
              </span>
              <span className={`text-[9px] uppercase tracking-wider ${selected ? 'text-primary' : 'text-muted-foreground'}`}>
                {PRESET_LABEL[key]}
              </span>
              {/* A ring rather than a 7px badge: the badge failed contrast at
                  that size, and the chip's aria-label carries it for AT. */}
              {isDefault && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded ring-1 ring-inset ring-primary/60"
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="relative pt-3.5">
        {/* -fg variants only: the raw --status-* hues are tuned for map
            symbology and drop to ~1.8:1 on the light theme's surfaces, which
            would make the ceiling marker invisible in daylight. */}
        {ceilingInBand && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-4 top-1.5 w-px bg-status-amber-fg"
            style={{ left: `calc(8px + (100% - 16px) * ${ceilingPercent / 100})` }}
          >
            <span className="absolute -top-0.5 left-1/2 max-w-[10rem] -translate-x-1/2 truncate text-[9px] uppercase tracking-wider text-status-amber-fg">
              {authority ? `${authority} · ` : ''}<span className="font-mono tabular-nums">{formatAltitude(profile.ceiling_m, unit)}</span>
            </span>
          </div>
        )}
        <input
          type="range"
          min={band.min}
          max={band.max}
          // Continuous on purpose. With a stepped range the browser silently
          // rounds any off-grid value onto the grid, so a preset like 76 m
          // would park the thumb at 75 while the field beside it read 76.
          step="any"
          value={displayValue}
          disabled={disabled}
          aria-label={label}
          // Without this a screen reader announces a bare "250" — and 250 ft
          // is a very different launch from 250 m.
          aria-valuetext={`${displayValue} ${unit} AGL`}
          onChange={(e) => commit(Number(e.target.value))}
          className={`h-6 w-full cursor-pointer disabled:opacity-50 ${overCeiling ? 'accent-status-amber-fg' : 'accent-primary'}`}
        />
        <div className="flex justify-between font-mono text-[9px] tabular-nums text-muted-foreground">
          <span>{band.min} {unit}</span>
          <span>{band.max} {unit}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="number"
          min={band.min}
          max={band.max}
          step={1}
          value={draft ?? displayValue}
          disabled={disabled}
          aria-label={`Exact ${label.toLowerCase()} in ${unit === 'ft' ? 'feet' : 'meters'}`}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDraft(); } }}
          className="w-[4.5rem] rounded border border-input bg-background px-2 py-1 text-right font-mono text-base font-semibold tabular-nums text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        />
        <span className="font-mono tabular-nums text-[10px] uppercase tracking-wider text-muted-foreground">{unit}</span>
        <div className="ml-auto text-right font-mono text-[10px] leading-relaxed tabular-nums text-muted-foreground">
          <p>≈ {converted}</p>
          {showRth && <p>RTH {formatAltitude(profile.rth_altitude_m, unit)}</p>}
        </div>
      </div>

      {footer}
    </div>
  );
}
